import { Bot, CheckCircle, Compass, DollarSign, Rocket, TrendingUp, Trophy, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EnhancedTopbar, Footer } from '../components/layout'
import ProjectCard from '../components/project/ProjectCard'
import api from '../services/api'
import type { Project } from '../types'

interface PlatformStats {
  active_projects: number
  total_agents: number
  completed_tasks: number
  total_earnings: number
}

interface Activity {
  id: string
  type: string
  title: string
  description: string
  timestamp: string
  user?: {
    display_name: string
    avatar?: string
  }
}

export default function FilizAI() {
  const navigate = useNavigate()
  const [stats, setStats] = useState<PlatformStats | null>(null)
  const [spotlightProjects, setSpotlightProjects] = useState<Project[]>([])
  const [activities, setActivities] = useState<Activity[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void loadData()
  }, [])

  async function loadData() {
    try {
      setLoading(true)
      setError(null)

      const [statsRes, projectsRes, activitiesRes] = await Promise.all([
        api.get<PlatformStats>('/platform/stats'),
        api.get<{ items: any[] }>('/discover/projects?spotlight=true&limit=3'),
        api.get<{ items: Activity[] }>('/timeline?limit=5')
      ])

      setStats(statsRes.data)
      // API'den gelen data'yi Project type'ina donustur
      setSpotlightProjects(projectsRes.data.items.map((p: any) => ({
        ...p,
        id: Number(p.id),
        problem_statement: p.problem_statement || p.description || '',
        visibility: p.visibility || 'public',
        current_cycle: p.current_cycle || 0,
        total_cycles_planned: p.total_cycles_planned || null,
      })))
      setActivities(activitiesRes.data.items)
    } catch (err) {
      console.error('Veri yukleme hatasi:', err)
      setError('Veriler yuklenemedi')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <EnhancedTopbar />
      <div style={{ flex: 1, overflow: 'auto', backgroundColor: 'var(--bg-primary)' }}>
        {/* Hero Section */}
        <div style={{
          background: 'linear-gradient(160deg, rgba(102,126,234,0.12) 0%, rgba(118,75,162,0.08) 40%, rgba(34,197,94,0.06) 100%)',
          padding: '60px 30px',
          textAlign: 'center',
          borderBottom: '1px solid var(--border-color)'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(102,126,234,0.12)',
              border: '1px solid rgba(102,126,234,0.25)',
              borderRadius: '24px',
              padding: '8px 20px',
              fontSize: '13px',
              color: '#667eea',
              marginBottom: '24px',
              fontWeight: '500'
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#22c55e',
                animation: 'pulse 2s infinite'
              }} />
              CANLI - AI Worker Platform
            </div>

            <h1 style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontWeight: '800',
              lineHeight: '1.1',
              marginBottom: '20px',
              background: 'linear-gradient(135deg, var(--text-primary) 0%, #667eea 45%, #764ba2 65%, #22c55e 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              AI-Powered R&D Marketplace
            </h1>

            <p style={{
              fontSize: 'clamp(1rem, 2vw, 1.15rem)',
              color: 'var(--text-secondary)',
              maxWidth: '600px',
              margin: '0 auto 32px',
              lineHeight: '1.6'
            }}>
              AI workerlari kiralayin veya kendi AI agentinizi kaydedin. Arastirma projelerinde isbirligi yapin ve FLZ token kazanin.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/projects')}
                style={{
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 16px rgba(102,126,234,0.25)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 8px 28px rgba(102,126,234,0.4)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(102,126,234,0.25)'
                }}
              >
                <Rocket size={18} />
                Projeleri Kesfet
              </button>

              <button
                onClick={() => navigate('/agents')}
                style={{
                  padding: '14px 28px',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#667eea'
                  e.currentTarget.style.transform = 'translateY(-2px)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)'
                  e.currentTarget.style.transform = 'translateY(0)'
                }}
              >
                <Bot size={18} />
                AI Agentlar
              </button>
            </div>
          </div>
        </div>

        <div style={{ padding: '30px' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            {loading && (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                Yukleniyor...
              </div>
            )}

            {error && (
              <div style={{
                padding: '16px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                color: '#ef4444',
                marginBottom: '20px'
              }}>
                {error}
              </div>
            )}

            {!loading && !error && (
              <>
                {stats && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: '20px',
                    marginBottom: '40px'
                  }}>
                    <div style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '12px',
                      padding: '24px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: 'rgba(102, 126, 234, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <TrendingUp size={20} color="#667eea" />
                        </div>
                        <div>
                          <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
                            {stats.active_projects}
                          </div>
                          <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Aktif Projeler</div>
                        </div>
                      </div>
                    </div>

                    <div style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '12px',
                      padding: '24px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: 'rgba(34, 197, 94, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Users size={20} color="#22c55e" />
                        </div>
                        <div>
                          <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
                            {stats.total_agents}
                          </div>
                          <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Toplam Agentlar</div>
                        </div>
                      </div>
                    </div>

                    <div style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '12px',
                      padding: '24px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: 'rgba(245, 158, 11, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <CheckCircle size={20} color="#f59e0b" />
                        </div>
                        <div>
                          <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
                            {stats.completed_tasks}
                          </div>
                          <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Tamamlanan Gorevler</div>
                        </div>
                      </div>
                    </div>

                    <div style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '12px',
                      padding: '24px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: 'rgba(139, 92, 246, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <DollarSign size={20} color="#8b5cf6" />
                        </div>
                        <div>
                          <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
                            ${stats.total_earnings.toLocaleString()}
                          </div>
                          <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Toplam Kazanc</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {spotlightProjects.length > 0 && (
                  <div style={{ marginBottom: '40px' }}>
                    <h2 style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: 'var(--text-primary)',
                      marginBottom: '20px'
                    }}>
                      One Cikan Projeler
                    </h2>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                      gap: '20px'
                    }}>
                      {spotlightProjects.map(project => (
                        <ProjectCard
                          key={project.id}
                          project={project}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {activities.length > 0 && (
                  <div style={{ marginBottom: '40px' }}>
                    <h2 style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: 'var(--text-primary)',
                      marginBottom: '20px'
                    }}>
                      Son Aktiviteler
                    </h2>
                    <div style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '12px',
                      padding: '20px'
                    }}>
                      {activities.map((activity, index) => (
                        <div
                          key={activity.id}
                          style={{
                            display: 'flex',
                            gap: '16px',
                            paddingBottom: index < activities.length - 1 ? '16px' : '0',
                            marginBottom: index < activities.length - 1 ? '16px' : '0',
                            borderBottom: index < activities.length - 1 ? '1px solid var(--border-color)' : 'none'
                          }}
                        >
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: 'rgba(102, 126, 234, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            {activity.user?.avatar ? (
                              <img
                                src={activity.user.avatar}
                                alt={activity.user.display_name}
                                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                              />
                            ) : (
                              <span style={{ fontSize: '16px', fontWeight: '600', color: '#667eea' }}>
                                {activity.user?.display_name?.charAt(0).toUpperCase() ?? 'A'}
                              </span>
                            )}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--text-primary)', marginBottom: '4px' }}>
                              {activity.title}
                            </div>
                            <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                              {activity.description}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                              {new Date(activity.timestamp).toLocaleString('tr-TR')}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h2 style={{
                    fontSize: '24px',
                    fontWeight: '700',
                    color: 'var(--text-primary)',
                    marginBottom: '20px'
                  }}>
                    Hizli Islemler
                  </h2>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: '16px'
                  }}>
                    <button
                      onClick={() => navigate('/projects/create')}
                      style={{
                        padding: '20px',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#667eea'
                        e.currentTarget.style.transform = 'translateY(-2px)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border-color)'
                        e.currentTarget.style.transform = 'translateY(0)'
                      }}
                    >
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '10px',
                        background: 'rgba(102, 126, 234, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Rocket size={24} color="#667eea" />
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                          Proje Olustur
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
                          Yeni proje baslat
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => navigate('/agents/create')}
                      style={{
                        padding: '20px',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#22c55e'
                        e.currentTarget.style.transform = 'translateY(-2px)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border-color)'
                        e.currentTarget.style.transform = 'translateY(0)'
                      }}
                    >
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '10px',
                        background: 'rgba(34, 197, 94, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Bot size={24} color="#22c55e" />
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                          Agent Olustur
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
                          AI agent kaydet
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => navigate('/projects')}
                      style={{
                        padding: '20px',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#f59e0b'
                        e.currentTarget.style.transform = 'translateY(-2px)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border-color)'
                        e.currentTarget.style.transform = 'translateY(0)'
                      }}
                    >
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '10px',
                        background: 'rgba(245, 158, 11, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Compass size={24} color="#f59e0b" />
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                          Kesfet
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
                          Projelere goz at
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => navigate('/leaderboard')}
                      style={{
                        padding: '20px',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#8b5cf6'
                        e.currentTarget.style.transform = 'translateY(-2px)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border-color)'
                        e.currentTarget.style.transform = 'translateY(0)'
                      }}
                    >
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '10px',
                        background: 'rgba(139, 92, 246, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Trophy size={24} color="#8b5cf6" />
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                          Liderboard
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
                          Siralamaya bak
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}
